import 'client-only';

import { toast } from '@ps/ui/atoms/sonner';

import type { PopClipVideoInfo } from './types';
import { type ClipReportReason, submitClipReport } from './report-clip';

interface HandleReportClipInput {
  video: PopClipVideoInfo;
  reason: ClipReportReason;
  /** The signed-in viewer's id, or undefined for a guest. */
  currentUserId: string | undefined;
  /** Opens the login modal for a guest (with the clip owner's brand chrome). */
  promptLogin: () => void;
}

/**
 * Shared handler for the in-player **Report** action, called from every clip
 * host's `reportVideo` branch (RN parity: guests log in first, then the report
 * is submitted). A guest → open login (they re-tap after signing in). A
 * logged-in viewer → POST the report via {@link submitClipReport} (server-side
 * `reports` write) and toast the RN success/error copy. Self-report / missing
 * owner just no-ops.
 */
export async function handleReportClip({
  video,
  reason,
  currentUserId,
  promptLogin,
}: HandleReportClipInput): Promise<void> {
  if (!currentUserId) {
    promptLogin();
    return;
  }
  const clipId = video.clipId ?? video.id;
  const targetId = video.author?.id;
  // No owner to attribute the report to, or a self-report — RN drops both.
  if (!clipId || !targetId || targetId === currentUserId) {
    return;
  }
  try {
    await submitClipReport({
      clipId,
      targetId,
      reason,
      content: video.videoURL,
    });
    toast.success('Thank you for your report, we will process it shortly');
  } catch (error) {
    console.error('[handleReportClip] report failed:', error);
    toast.error('Could not submit your report. Please try again.');
  }
}
